/* eslint no-param-reassign:0 */
import { forwardRef } from 'react';

import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import { styled } from '@zenbusiness/zui/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';

const FlexRowRoot = styled('div', {
  name: 'FlexRow',
  slot: 'Root'
})`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: stretch;

  &.FlexRow-wrap {
    flex-wrap: wrap;
  }

  &.FlexRow-center {
    align-items: center;
    justify-content: center;
  }

  &.FlexRow-justify-left {
    justify-content: flex-start;
  }

  &.FlexRow-justify-center {
    justify-content: center;
  }

  &.FlexRow-justify-right {
    justify-content: flex-end;
  }

  &.FlexRow-justify-between {
    justify-content: space-between;
  }

  &.FlexRow-justify-around {
    justify-content: space-around;
  }

  &.FlexRow-justify-stretch {
    justify-content: stretch;
  }

  &.FlexRow-align-top {
    align-items: flex-start;
  }

  &.FlexRow-align-center {
    align-items: center;
  }

  &.FlexRow-align-bottom {
    align-items: flex-end;
  }

  &.FlexRow-align-stretch {
    align-items: stretch;
  }
`;

const Row = forwardRef(function FlexRow(
  {
    center,
    justify,
    align,
    className,
    component,
    wrap = false,
    style,
    grow,
    shrink,
    basis,
    auto,
    fill,
    children
  },
  ref
) {
  const Component = component || 'div';

  style = {
    ...style
  };

  if (grow === true) {
    grow = 1;
  }
  if (grow === false) {
    grow = 0;
  }
  if (shrink === true) {
    shrink = 1;
  }
  if (shrink === false) {
    shrink = 0;
  }

  if (fill) {
    style.flex = `1 1 ${basis || 'auto'}`;
  } else {
    if (!isUndefinedOrNull(grow)) {
      style.flexGrow = grow;
    }
    if (!isUndefinedOrNull(shrink)) {
      style.flexShrink = shrink;
    }
    if (basis) {
      style.flexBasis = basis;
    } else if (auto) {
      style.flexBasis = 'auto';
    }
  }

  return (
    <FlexRowRoot
      as={Component}
      className={clsx(
        className,
        center && 'FlexRow-center',
        justify && `FlexRow-justify-${justify}`,
        align && `FlexRow-align-${align}`,
        wrap && 'FlexRow-wrap'
      )}
      ref={ref}
      style={style}
    >
      {children}
    </FlexRowRoot>
  );
});
Row.propTypes = {
  component: PropTypes.elementType,

  wrap: PropTypes.bool,

  center: PropTypes.bool,

  justify: PropTypes.oneOf(['left', 'center', 'right', 'between', 'around', 'stretch']),

  align: PropTypes.oneOf(['top', 'center', 'bottom', 'stretch']),

  grow: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),

  shrink: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),

  basis: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  auto: PropTypes.bool,

  fill: PropTypes.bool
};

export default Row;
